import axios from 'axios';
import {Form} from '@/plugins/FWForms/index';
import * as types from '../mutation-types';

export interface TollState {
    init: boolean;
    loading: Record<string, boolean>;
    tollForm: Form<any>;
    tollAgencies: any[];
    tollAccounts: any[];
    wishlist: any[];
    transactions: any[];
    transponders: any[];
}

const state: TollState = {
    init: false,
    loading: {
        connect_account: false,
        get_tolls: false,
        toggle_tolls: false,
        add_wishlist: false,
        attach_booking: false,
        attach_vehicle: false,
        detach_booking: false,
        detach_vehicle: false,
        delete_account: false,
        transaction_dismissing: false,
        manual_fetch_transactions: false,
        fetch_transponders: false,
    },
    tollForm: new Form(),
    tollAgencies: [],
    tollAccounts: [],
    wishlist: [],
    transactions: [],
    transponders: [],
};

const getters = {
    init: (state: TollState) => state.init,
    loading: (state: TollState) => state.loading,
    tollAgencies: (state: TollState) => state.tollAgencies,
    tollAccounts: (state: TollState) => state.tollAccounts,
    tollForm: (state: TollState) => state.tollForm,
    wishlist: (state: TollState) => state.wishlist,
    transactions: (state: TollState) => state.transactions,
    transponders: (state: TollState) => state.transponders,
};

const mutations = {
    [types.SET_TOLLS_LOADING](state: TollState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_TOLL_AGENCIES](state: TollState, tolls) {
        state.tollAgencies = tolls;
    },
    [types.SET_TOLL_ACCOUNTS](state: TollState, tolls) {
        state.tollAccounts = tolls;
    },
    [types.SET_TOLL_AGENCIES_WISHLIST](state: TollState, tolls) {
        state.wishlist = tolls;
    },
    [types.SET_TOLL_TRANSACTIONS](state: TollState, transactions) {
        state.transactions = transactions;
    },
    [types.SET_TOLL_TRANSPONDERS](state: TollState, transponders) {
        state.transponders = transponders;
    },
};

const actions = {
    getTollAgencies({commit}) {
        commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: true});
        state.tollForm.errors.clear();

        axios.get('/v1/toll-agencies')
            .then((response) => {
                commit('SET_TOLL_AGENCIES', response?.data?.tollAgencies ?? []);
            }).catch((e) => {
            state.tollForm.errors.set(e?.response?.data?.errors ?? []);
        }).finally(() => {
            commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: false});
        });
    },

    getTollAgenciesWishlist({commit}) {
        commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: true});

        axios.get('/v1/toll-agencies/wishlist')
            .then((response) => {
                commit('SET_TOLL_AGENCIES_WISHLIST', response?.data?.wishlist ?? []);
            }).catch(() => {
        }).finally(() => {
            commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: false});
        });
    },

    async connectTollAgency({commit}, data): Promise<boolean> {
        commit('SET_TOLLS_LOADING', {subject: 'connect_account', loading: true});

        try {
            const response = await axios.post('/v1/toll-accounts', data);
            commit('SET_TOLL_ACCOUNTS', response?.data?.tollAccounts ?? []);

            return true;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                state.tollForm.errors.set((e.response?.data as { errors?: Record<string, string[]> })?.errors ?? {});
            }

            return false;
        } finally {
            commit('SET_TOLLS_LOADING', {subject: 'connect_account', loading: false});
        }
    },

    getTollAccounts({commit}) {
        commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: true});

        axios.get('/v1/toll-accounts')
            .then((response) => {
                commit('SET_TOLL_ACCOUNTS', response?.data?.tollAccounts ?? []);
            }).catch(() => {
        }).finally(() => {
            commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: false});
        });
    },

    async toggleTollAccountActive({commit}, tollAccount): Promise<boolean> {
        commit('SET_TOLLS_LOADING', {subject: 'toggle_tolls', loading: true});

        try {
            const response = await axios.put(`/v1/toll-accounts/${tollAccount.uuid}/toggle-active`);
            commit('SET_TOLL_ACCOUNTS', response?.data?.tollAccounts ?? []);

            return true;
        } catch {
            return false;
        } finally {
            commit('SET_TOLLS_LOADING', {subject: 'toggle_tolls', loading: false});
        }
    },

    async deleteTollAccount({commit}, tollAccount): Promise<boolean> {
        commit('SET_TOLLS_LOADING', {subject: 'delete_account', loading: true});

        try {
            const response = await axios.delete(`/v1/toll-accounts/${tollAccount.uuid}`);
            commit('SET_TOLL_ACCOUNTS', response?.data?.tollAccounts ?? []);

            return true;
        } catch {
            return false;
        } finally {
            commit('SET_TOLLS_LOADING', {subject: 'delete_account', loading: false});
        }
    },

    testTollAccount({commit}, tollAccount) {
        commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: true});

        axios.post(`/v1/toll-accounts/${tollAccount.uuid}/test`)
            .then(() => {
            }).catch(() => {
        }).finally(() => {
            commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: false});
        });
    },

    async updateAccount({commit}, tollAccount): Promise<boolean> {
        commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: true});

        try {
            await axios.put(`/v1/toll-accounts/${tollAccount.uuid}`, tollAccount);

            return true;
        } catch {
            return false;
        } finally {
            commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: false});
        }
    },

    fetchTransactions({commit}, tollAccount) {
        commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: true});

        axios.get(`/v1/toll-accounts/${tollAccount.uuid}/transactions`)
            .then(() => {
            }).catch(() => {
        }).finally(() => {
            commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: false});
        });
    },

    async manualFetchAllTransactions({commit}): Promise<boolean> {
        commit('SET_TOLLS_LOADING', {subject: 'manual_fetch_transactions', loading: true});

        try {
            const response = await axios.get('/v1/toll-accounts/transactions/manual-fetch');
            commit('SET_TOLL_TRANSACTIONS', response?.data?.transactions ?? []);

            return true;
        } catch {
            return false;
        } finally {
            commit('SET_TOLLS_LOADING', {subject: 'manual_fetch_transactions', loading: false});
        }
    },

    fetchVehicles({commit}, tollAccount) {
        commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: true});

        axios.get(`/v1/toll-accounts/${tollAccount.uuid}/vehicles`)
            .then(() => {
            }).catch(() => {
        }).finally(() => {
            commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: false});
        });
    },

    fetchTransponders({commit}, tollAccount) {
        commit('SET_TOLLS_LOADING', {subject: 'fetch_transponders', loading: true});

        axios.get(`/v1/toll-accounts/${tollAccount.uuid}/transponders`)
            .then((response) => {
                commit('SET_TOLL_TRANSPONDERS', response?.data?.transponders ?? []);
            }).catch(() => {
        }).finally(() => {
            commit('SET_TOLLS_LOADING', {subject: 'fetch_transponders', loading: false});
        });
    },

    saveTransponders({commit}, transponders) {
        commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: true});

        axios.put('/v1/toll-accounts/transponders', transponders)
            .then((response) => {
                commit('SET_TOLL_TRANSPONDERS', response?.data?.transponders ?? []);
            }).catch(() => {
        }).finally(() => {
            commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: false});
        });
    },

    getTransactions({commit}) {
        commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: true});

        axios.get('/v1/toll-accounts/transactions')
            .then((response) => {
                commit('SET_TOLL_TRANSACTIONS', response?.data?.transactions ?? []);
            }).catch(() => {
        }).finally(() => {
            commit('SET_TOLLS_LOADING', {subject: 'get_tolls', loading: false});
        });
    },

    async removeTransactionsMatchedBooking({commit}, transaction): Promise<{ transaction: any } | undefined> {
        commit('SET_TOLLS_LOADING', {subject: 'detach_booking', loading: true});

        try {
            const response = await axios.delete(`/v1/toll-transactions/${transaction.uuid}/matched-booking`);
            commit('SET_TOLL_TRANSACTIONS', response?.data?.transactions ?? []);

            return {transaction: response?.data?.transaction ?? []};
        } catch {
            return undefined;
        } finally {
            commit('SET_TOLLS_LOADING', {subject: 'detach_booking', loading: false});
        }
    },

    async removeTransactionsMatchedVehicle({commit}, transaction): Promise<{ transaction: any } | undefined> {
        commit('SET_TOLLS_LOADING', {subject: 'detach_vehicle', loading: true});

        try {
            const response = await axios.delete(`/v1/toll-transactions/${transaction.uuid}/matched-vehicle`);
            commit('SET_TOLL_TRANSACTIONS', response?.data?.transactions ?? []);

            return {transaction: response?.data?.transaction};
        } catch {
            return undefined;
        } finally {
            commit('SET_TOLLS_LOADING', {subject: 'detach_vehicle', loading: false});
        }
    },

    async dismissTransaction({commit}, transaction): Promise<boolean> {
        commit('SET_TOLLS_LOADING', {subject: 'transaction_dismissing', loading: true});

        try {
            const response = await axios.put(`/v1/toll-transactions/${transaction.uuid}/dismiss`);
            commit('SET_TOLL_TRANSACTIONS', response?.data?.transactions ?? []);

            return true;
        } catch {
            return false;
        } finally {
            commit('SET_TOLLS_LOADING', {subject: 'transaction_dismissing', loading: false});
        }
    },

    async attachTransactionsMatchedBooking({commit}, data): Promise<{ transaction: any } | undefined> {
        commit('SET_TOLLS_LOADING', {subject: 'attach_booking', loading: true});

        try {
            const response = await axios.put(`/v1/toll-transactions/${data.transaction.uuid}/matched-booking`, {b_id: data.b_id});
            commit('SET_TOLL_TRANSACTIONS', response?.data?.transactions ?? []);

            return {transaction: response?.data?.transaction ?? []};
        } catch {
            return undefined;
        } finally {
            commit('SET_TOLLS_LOADING', {subject: 'attach_booking', loading: false});
        }
    },

    async attachTransactionsMatchedVehicle({commit}, data): Promise<{ transaction: any } | undefined> {
        commit('SET_TOLLS_LOADING', {subject: 'attach_vehicle', loading: true});

        try {
            const response = await axios.put(`/v1/toll-transactions/${data.transaction.uuid}/matched-vehicle`, {l_id: data.l_id});
            commit('SET_TOLL_TRANSACTIONS', response?.data?.transactions ?? []);

            return {transaction: response?.data?.transaction};
        } catch {
            return undefined;
        } finally {
            commit('SET_TOLLS_LOADING', {subject: 'attach_vehicle', loading: false});
        }
    },

    async submitTollWishlist({commit}, data): Promise<{ added?: boolean, alreadySupported?: any } | undefined> {
        commit('SET_TOLLS_LOADING', {subject: 'add_wishlist', loading: true});

        try {
            const response = await axios.post('/v1/toll-agencies/wishlist', data);
            commit('SET_TOLL_AGENCIES_WISHLIST', response?.data?.wishlist ?? []);

            return {added: true};
        } catch (e) {
            if (!axios.isAxiosError(e)) {
                return undefined;
            }

            const responseData = e.response?.data as {
                errors?: Record<string, string[]>,
                code?: string,
                tollAgency?: any
            } | undefined;
            state.tollForm.errors.set(responseData?.errors ?? {});

            if (responseData?.code === 'already_supported') {
                return {alreadySupported: responseData.tollAgency};
            }

            return undefined;
        } finally {
            commit('SET_TOLLS_LOADING', {subject: 'add_wishlist', loading: false});
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
